import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Droppable } from 'react-beautiful-dnd';
import ReactQuill from 'react-quill';
import Joi from 'joi';

import {
    useGetQuestionsQuery,
    useGetTunnelStepQuery,
    useUpdateCustomTunnelStepMutation,
} from 'api/main';
import { customTunnelSelectors } from 'reducers';
import {
    editingAnswerIdSet,
    editingQuestionIdSet,
    editingQuestionSet,
    editingStepIdSet,
} from 'reducers/customTunnel';
import { useDialog } from 'utils/hooks';
import { questionType, quillFormats } from 'utils/constants';
import { validateSchema } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import QuestionItem from './QuestionItem';
import {
    ActionButton,
    CancelButton,
    ConfirmDialog,
    CustomFormLabel,
    CustomTextField,
    Loader,
} from 'components';
import { Box } from '@mui/material';

const schema = {
    title: Joi.string().required().trim().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
};

const UpdateCustomStep = () => {
    const dispatch = useDispatch();
    const { open: confirmSaveOpen, onClose: handleSaveClose, onOpen: handleSaveOpen } = useDialog();

    const { ppId, pmId } = useSelector(customTunnelSelectors.getEditingStepId);
    const newEditingQuestionId = useSelector(customTunnelSelectors.getNewEditingQuestionId);
    const editingQuestion = useSelector(customTunnelSelectors.getEditingQuestion);
    const editingAnswerId = useSelector(customTunnelSelectors.getEditingAnswerId);

    const { data: ppTunnelStep, isLoading: ppStepLoading } = useGetTunnelStepQuery(ppId, {
        skip: !ppId,
    });

    const { data: ppStepQuestions = [], isLoading: ppQuestionsLoading } = useGetQuestionsQuery(
        { tunnelStepId: ppId, pagination: false },
        { skip: !ppId }
    );

    const { data: pmStepQuestions = [], isLoading: pmQuestionsLoading } = useGetQuestionsQuery(
        { tunnelStepId: pmId, pagination: false },
        { skip: !pmId }
    );
    const [updateTunnelStep, { isLoading: updateLoading, isSuccess: updateSuccess }] =
        useUpdateCustomTunnelStepMutation();

    const [form, setForm] = useState({ title: '', subtitle: '', questions: [] });
    const [errors, setErrors] = useState({ title: '' });

    const handleChange = useCallback(
        key => e => {
            setForm(prevState => ({ ...prevState, [key]: e.target.value }));
        },
        []
    );

    const handleChangeSubtitle = useCallback(value => {
        setForm(prevState => ({ ...prevState, subtitle: value }));
    }, []);

    const handleAddQuestion = useCallback(() => {
        dispatch(editingQuestionIdSet(null));
        dispatch(
            editingQuestionSet({
                value: {
                    title: '',
                    mandatory: true,
                    type: questionType.RADIO,
                    answers: [],
                },
            })
        );
        setForm(prevState => ({
            ...prevState,
            questions: [
                ...prevState.questions,
                {
                    title: '',
                    mandatory: true,
                    type: questionType.RADIO,
                    answers: [],
                },
            ],
        }));
    }, [dispatch]);

    const handleRemoveQuestion = useCallback(
        index => () => {
            setForm(prevState => {
                let newQuestions = [...prevState.questions];
                newQuestions.splice(index, 1);
                return {
                    ...prevState,
                    questions: newQuestions,
                };
            });
        },
        []
    );

    const handleUpdateQuestion = useCallback(
        index => newQuestion => {
            setForm(prevState => {
                let newQuestions = [...prevState.questions];
                newQuestions.splice(index, 1, newQuestion);
                return {
                    ...prevState,
                    questions: newQuestions,
                };
            });
        },
        []
    );

    const handleCleanupQuestions = useCallback(() => {
        setForm(prevState => ({
            ...prevState,
            questions: prevState.questions.filter(q => q.id || q.title),
        }));
    }, []);

    const handleCancel = useCallback(() => {
        dispatch(editingStepIdSet(null));
        dispatch(editingQuestionIdSet(null));
        dispatch(editingQuestionSet(null));
        dispatch(editingAnswerIdSet(null));
    }, [dispatch]);

    const handleSave = useCallback(() => {
        updateTunnelStep({
            ppId,
            pmId,
            title: form.title,
            description: form.subtitle,
        });
    }, [form, ppId, updateTunnelStep, pmId]);

    const handleConfirmSave = useCallback(() => {
        const { newErrors, valid } = validateSchema(schema, form);
        if (valid) {
            setErrors({ title: '' });
            if (editingQuestion || editingAnswerId) {
                handleSaveOpen();
            } else {
                handleSave();
            }
        } else {
            setErrors(newErrors);
        }
    }, [editingAnswerId, editingQuestion, form, handleSave, handleSaveOpen]);

    useEffect(() => {
        if (ppId && ppTunnelStep?.id) {
            // use saved data
            setForm({
                title: ppTunnelStep.title,
                subtitle: ppTunnelStep.description,
                questions: ppStepQuestions,
            });
        }
    }, [ppId, ppTunnelStep, ppStepQuestions]);

    useEffect(() => {
        if (updateSuccess) {
            dispatch(editingStepIdSet(null));
        }
    }, [dispatch, updateSuccess]);

    return (
        <Box
            sx={theme => ({
                border: `3px solid ${theme.palette.primary.main}`,
                borderRadius: '5px',
                marginY: 1,
                padding: theme.spacing(2, 3),
            })}
        >
            <Loader loading={ppStepLoading || ppQuestionsLoading || pmQuestionsLoading}>
                <CustomTextField
                    label={texts.STEP_TITLE}
                    value={form.title}
                    onChange={handleChange('title')}
                    error={errors.title}
                />
                <CustomFormLabel>{texts.STEP_SUBTITLE}</CustomFormLabel>
                <ReactQuill
                    className="small"
                    value={form.subtitle}
                    onChange={handleChangeSubtitle}
                    formats={quillFormats}
                />
                <Box textAlign="center" mb={1}>
                    <ActionButton
                        variant="outlined"
                        sx={{ padding: '5px 8px' }}
                        onClick={handleAddQuestion}
                        disabled={
                            !form.questions.every(q => q.title) ||
                            !!newEditingQuestionId ||
                            !!editingQuestion
                        }
                    >
                        {texts.ADD_QUESTION}
                    </ActionButton>
                </Box>
                <Droppable
                    droppableId={`droppable-questions-${ppTunnelStep?.id || 0}`}
                    type={`question-${ppTunnelStep?.id || 0}`}
                >
                    {(provided, snapshot) => (
                        <Box
                            paddingY={2}
                            {...provided.droppableProps}
                            sx={{ opacity: snapshot.isDraggingOver ? '.85' : 1 }}
                            ref={provided.innerRef}
                        >
                            {form.questions.map((q, idx) => (
                                <QuestionItem
                                    key={`${q.title}-${idx}`}
                                    index={idx}
                                    question={q}
                                    deleteLoading={updateLoading}
                                    ppQuestions={ppStepQuestions}
                                    pmQuestions={pmStepQuestions}
                                    onRemove={handleRemoveQuestion(idx)}
                                    onUpdate={handleUpdateQuestion(idx)}
                                    formCleanup={handleCleanupQuestions}
                                />
                            ))}
                            {provided.placeholder}
                        </Box>
                    )}
                </Droppable>
                <Box display="flex" flexDirection="column" alignItems="center" gap={1} paddingY={2}>
                    <ConfirmDialog
                        open={confirmSaveOpen}
                        handleConfirm={handleSave}
                        handleClose={handleSaveClose}
                        title={texts.CHANGES_NOT_CONFIRMED}
                        confirmText={texts.CONFIRMER}
                        cancelText={texts.CANCEL}
                    >
                        {texts.CONFIRM_DISCARD_CHANGES}
                    </ConfirmDialog>
                    <ActionButton
                        variant="contained"
                        loading={updateLoading}
                        onClick={handleConfirmSave}
                    >
                        {texts.SAVE}
                    </ActionButton>
                    <CancelButton disabled={updateLoading} onClick={handleCancel}>
                        {texts.CANCEL}
                    </CancelButton>
                </Box>
            </Loader>
        </Box>
    );
};

export default UpdateCustomStep;
